/**
 * QRInvitePoster — printable, share-anywhere QR poster for a family
 * tree. Renders an A4/Letter-friendly poster with:
 *   - Tree name (headline)
 *   - Sender line ("Invited by …")
 *   - Big QR pointing to /family/{slug} (or /join/{slug} when a per-
 *     recipient token is provided)
 *   - Short URL below the QR so anyone can also type it
 *
 * Two entry-points from Bulk Invite:
 *   - Batch mode → one poster for the whole batch (public tree URL).
 *   - Per-recipient mode → separate personalised posters per invitee.
 *
 * Uses `qrcode` (client-side generation), `html2canvas` for the PNG
 * download, and window.print() for direct printing.
 */
import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import { Download, Printer, X } from "lucide-react";

/**
 * @param {object} props
 * @param {string} props.treeName    display name
 * @param {string} props.treeSlug    used in the QR URL
 * @param {string} [props.senderName]
 * @param {Array<{email?:string, name?:string, token?:string}>} [props.recipients]
 *        When provided, we generate one poster per recipient with a
 *        personalised /join/{slug}?email=&token= link (falls back to
 *        the batch URL when the token is missing).
 * @param {string} [props.baseUrl]   defaults to window.location.origin
 * @param {() => void} props.onClose
 */
export default function QRInvitePoster({
  treeName,
  treeSlug,
  senderName,
  recipients = null,
  baseUrl,
  onClose,
}) {
  const origin = baseUrl || (typeof window !== "undefined" ? window.location.origin : "https://indianfamilytree.com");
  // Batch mode — a single poster targeting the public tree.
  const items = recipients && recipients.length
    ? recipients.map((r) => ({
        title: r.name || (r.email || r.phone || "Family member"),
        subtitle: r.email || (r.phone ? `+${r.phone}` : ""),
        url: r.email && r.token
          ? `${origin}/join/${treeSlug}?email=${encodeURIComponent(r.email)}&token=${r.token}`
          : `${origin}/family/${treeSlug}`,
      }))
    : [{ title: null, subtitle: null, url: `${origin}/family/${treeSlug}` }];

  const [idx, setIdx] = useState(0);
  const cur = items[idx];
  const posterRef = useRef(null);
  const [qrDataUrl, setQrDataUrl] = useState("");

  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(cur.url, {
      errorCorrectionLevel: "H",
      margin: 1,
      scale: 12,
      color: { dark: "#3D2A17", light: "#FFFFFF" },
    })
      .then((url) => { if (!cancelled) setQrDataUrl(url); })
      .catch(() => { if (!cancelled) setQrDataUrl(""); });
    return () => { cancelled = true; };
  }, [cur.url]);

  const download = async () => {
    if (!posterRef.current) return;
    try {
      const canvas = await html2canvas(posterRef.current, { background: "#FFFDF8", scale: 2 });
      const link = document.createElement("a");
      link.download = `qr-invite-${treeSlug}${cur.title ? "-" + cur.title.replace(/\W+/g, "_") : ""}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Poster downloaded.");
    } catch (e) {
      toast.error("Couldn't render the poster — try Print instead.");
    }
  };

  const doPrint = () => {
    // Isolate the poster on a print window so browser chrome / page
    // wrappers don't pollute the printed page.
    if (!posterRef.current) return window.print();
    const html = posterRef.current.outerHTML;
    const w = window.open("", "qr-poster-print", "width=800,height=1100");
    if (!w) { toast.error("Please allow pop-ups to print."); return; }
    w.document.write(`
      <!doctype html>
      <html><head><title>${treeName} — Family Invite</title>
      <style>
        @page { size: A4; margin: 12mm; }
        body { margin: 0; padding: 0; background: #FFFDF8; font-family: -apple-system, "Segoe UI", sans-serif; }
      </style></head>
      <body>${html}</body></html>
    `);
    w.document.close();
    // Give the QR image a beat to load in the print window.
    setTimeout(() => { try { w.focus(); w.print(); } catch { /* noop */ } }, 300);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-2 md:px-4" onClick={onClose} data-testid="qr-invite-poster">
      <div
        className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Toolbar */}
        <div className="flex items-center justify-between px-4 md:px-6 py-3 border-b" style={{ borderColor: "var(--wofy-border)" }}>
          <div>
            <div className="text-sm font-semibold" style={{ color: "var(--wofy-primary)" }}>QR invite poster</div>
            {items.length > 1 && (
              <div className="text-[11px] opacity-70">
                Poster {idx + 1} of {items.length}
                {cur.title ? ` · for ${cur.title}` : ""}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1">
            {items.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={() => setIdx((i) => Math.max(0, i - 1))}
                  disabled={idx === 0}
                  className="wofy-btn-ghost !px-2 disabled:opacity-40"
                  data-testid="qr-poster-prev"
                >
                  ‹
                </button>
                <button
                  type="button"
                  onClick={() => setIdx((i) => Math.min(items.length - 1, i + 1))}
                  disabled={idx === items.length - 1}
                  className="wofy-btn-ghost !px-2 disabled:opacity-40"
                  data-testid="qr-poster-next"
                >
                  ›
                </button>
              </>
            )}
            <button
              type="button"
              onClick={download}
              className="wofy-btn-ghost text-xs inline-flex items-center gap-1"
              data-testid="qr-poster-download"
            >
              <Download className="w-3.5 h-3.5" /> PNG
            </button>
            <button
              type="button"
              onClick={doPrint}
              className="wofy-btn-primary text-xs inline-flex items-center gap-1"
              data-testid="qr-poster-print"
            >
              <Printer className="w-3.5 h-3.5" /> Print
            </button>
            <button
              type="button"
              onClick={onClose}
              className="wofy-btn-ghost !p-1"
              data-testid="qr-poster-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Poster canvas — this is what gets printed/downloaded. */}
        <div className="px-3 md:px-6 py-4">
          <div
            ref={posterRef}
            data-testid="qr-poster-canvas"
            style={{
              background: "linear-gradient(135deg, #FFFDF8, #FFF4E1)",
              padding: 36,
              border: "1px solid #E8DFCF",
              borderRadius: 14,
              textAlign: "center",
              fontFamily: '-apple-system, "Segoe UI", sans-serif',
              color: "#3D2A17",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Decorative corners */}
            <span aria-hidden style={{ position: "absolute", top: 12, left: 12, right: 12, bottom: 12, border: "2px solid #D5B27A", borderRadius: 10, pointerEvents: "none", opacity: 0.6 }} />
            <div style={{ fontSize: 11, letterSpacing: "0.24em", textTransform: "uppercase", color: "#B25D38", marginBottom: 12 }}>
              You're invited
            </div>
            <h1 style={{ margin: "0 0 4px 0", fontSize: 32, fontWeight: 700, color: "#B25D38" }}>
              {treeName}
            </h1>
            <div style={{ fontSize: 13, opacity: 0.75, marginBottom: 22 }}>
              {senderName ? `Invited by ${senderName}` : "IndianFamilyTree.com"}
              {cur.title ? ` · ${cur.title}` : ""}
            </div>

            {qrDataUrl ? (
              <img
                src={qrDataUrl}
                alt={`QR code for ${cur.url}`}
                style={{ width: 260, height: 260, background: "#fff", padding: 8, borderRadius: 10, boxShadow: "0 2px 12px rgba(178,93,56,0.15)" }}
                data-testid="qr-poster-image"
              />
            ) : (
              <div style={{ width: 260, height: 260, margin: "0 auto", background: "#eee", borderRadius: 10, display: "grid", placeItems: "center", color: "#999" }}>
                Generating QR…
              </div>
            )}

            <div style={{ marginTop: 18, fontSize: 14 }}>
              Scan with your phone camera to open the tree.
            </div>
            <div style={{ marginTop: 6, fontSize: 12, color: "#6B4E2E", wordBreak: "break-all", padding: "0 12px" }}>
              {cur.url}
            </div>

            <div style={{ marginTop: 28, fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "#B25D38", opacity: 0.75 }}>
              IndianFamilyTree.com · Discover Your Roots
            </div>
          </div>

          {items.length > 1 && (
            <div className="mt-3 text-[11px] opacity-70 text-center">
              Tip: use ‹ › to preview each relative's personal poster, then Print or Download all of them.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
